import { Button } from "@frontend/components/ui/button"
import { Checkbox } from "@frontend/components/ui/checkbox"
import { Label } from "@frontend/components/ui/label"
import { Slider } from "@frontend/components/ui/slider"
import { ToggleGroup, ToggleGroupItem } from "@frontend/components/ui/toggle-group"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@frontend/components/ui/tooltip"
import { useToggleSet } from "@frontend/hooks/use-toggle-set"
import { flattenMaskLabels, normalizeMaskGroups } from "@frontend/pages/case-workspace/radiology/lib/mask-utils"
import type { MaskGroup, MaskLabel, MaskSchema } from "@frontend/pages/case-workspace/radiology/types"
import type { MaskAnnotationState } from "@shared/lib/history-types"
import { deriveInstanceColor } from "@shared/lib/mask-colormap"
import { Brush, ChevronDown, ChevronRight, Eraser, Eye, EyeOff, Info, PenLine, Sparkles, Trash2 } from "lucide-react"
import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from "react"

type MaskTool = "paint" | "erase" | "sam_box" | "sam_scribble"

const MASK_TOOL_ITEMS: Array<{ value: MaskTool; icon: typeof Brush; tip: string }> = [
  { value: "paint", icon: Brush, tip: "Paint (B) — brush tool" },
  { value: "erase", icon: Eraser, tip: "Eraser (E)" },
  { value: "sam_box", icon: Sparkles, tip: "SAM Box — AI segmentation" },
  { value: "sam_scribble", icon: PenLine, tip: "SAM Scribble — AI scribble segmentation" }
]

function CollapsibleSectionHeader({
  collapsed,
  onToggle,
  label,
  labelClassName,
  count
}: {
  collapsed: boolean
  onToggle: () => void
  label: string
  labelClassName?: string
  count?: number
}) {
  return (
    <button className="flex items-center gap-1 w-full text-left" onClick={onToggle}>
      {collapsed ? (
        <ChevronRight className="size-3 text-muted-foreground" />
      ) : (
        <ChevronDown className="size-3 text-muted-foreground" />
      )}
      <span className={labelClassName ?? "text-[10px] font-medium text-muted-foreground uppercase tracking-wide"}>
        {label}
      </span>
      {count != null && count > 0 && <span className="text-[10px] text-muted-foreground tabular-nums">({count})</span>}
    </button>
  )
}

function MaskInstanceCard({
  mask,
  labelDef,
  isActive,
  opacityPercent,
  isEditable,
  onSelect,
  onDelete,
  onVisibilityToggle,
  onOpacityChange,
  onOpacityCommit
}: {
  mask: MaskAnnotationState
  labelDef: MaskLabel | null
  isActive: boolean
  opacityPercent: number
  isEditable: boolean
  onSelect: () => void
  onDelete: (id: string) => void
  onVisibilityToggle: () => void
  onOpacityChange: (percent: number) => void
  onOpacityCommit: (percent: number) => void
}) {
  const cardRef = useRef<HTMLDivElement>(null)
  const isVisible = opacityPercent > 0
  const labelColor = labelDef ? deriveInstanceColor(labelDef.color, mask.instanceOrdinal) : "#888"
  const labelText = `${labelDef?.label ?? mask.labelName ?? "Mask"} ${mask.instanceOrdinal}`

  useEffect(() => {
    if (isActive && cardRef.current) {
      cardRef.current.scrollIntoView({ block: "nearest" })
    }
  }, [isActive])

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSelect()
        }
      }}
      className={`rounded-md border px-2.5 py-2 cursor-pointer transition-colors ${
        isActive ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border bg-surface-raised"
      } ${isVisible ? "" : "opacity-80"}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="size-3 rounded-sm shrink-0 border border-white/20" style={{ backgroundColor: labelColor }} />
        <span className="flex-1 min-w-0 text-[11px] font-medium truncate">{labelText}</span>
        <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{opacityPercent}%</span>
        <Button
          size="icon"
          variant="ghost"
          className="size-6"
          disabled={!isEditable}
          title={isVisible ? "Hide mask" : "Show mask"}
          onClick={(e) => {
            e.stopPropagation()
            onVisibilityToggle()
          }}
        >
          {isVisible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="size-6 text-destructive"
          disabled={!isEditable}
          title="Delete mask"
          onClick={(e) => {
            e.stopPropagation()
            if (!isEditable) return
            if (!window.confirm("Delete this mask instance?")) return
            onDelete(mask.id)
          }}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stop slider clicks from toggling card selection */}
      <div className="mt-2" onClick={(e) => e.stopPropagation()}>
        <Slider
          value={[opacityPercent]}
          min={0}
          max={100}
          step={1}
          disabled={!isEditable}
          onValueChange={(value) => {
            const next = Array.isArray(value) ? (value[0] ?? 0) : value
            onOpacityChange(next)
          }}
          onValueCommitted={(value) => {
            const next = Array.isArray(value) ? (value[0] ?? 0) : value
            onOpacityCommit(next)
          }}
        />
      </div>
    </div>
  )
}

interface MaskModulePanelProps {
  schema: MaskSchema
  moduleId: string
  seriesId: string | null
  masks: MaskAnnotationState[]
  activeMaskInstanceId: string | null
  onSelectMaskInstance: (id: string | null) => void
  onActivateSingleLabel: (labelKey: string) => void
  onCreateMaskInstance: (labelKey: string) => void
  onDeleteMaskInstance: (id: string) => void
  drawingDisabled: boolean
  onDrawingDisabled: Dispatch<SetStateAction<boolean>>
  brushSize: number[]
  onBrushSize: Dispatch<SetStateAction<number[]>>
  filledPen: boolean
  onFilledPen: Dispatch<SetStateAction<boolean>>
  eraseMode: boolean
  onEraseMode: Dispatch<SetStateAction<boolean>>
  samMode: boolean
  onSamMode: (active: boolean) => void
  samAvailable: boolean
  samPending: boolean
  samScribbleMode: boolean
  onSamScribbleMode: (active: boolean) => void
  samConfidence: number
  onSamConfidence: (value: number) => void
  maskOpacityById: Record<string, number>
  onMaskOpacityChange: (maskId: string, percent: number) => void
  onMaskOpacityCommit: (maskId: string, percent: number) => void
  onMaskVisibilityToggle: (maskId: string) => void
  isEditable: boolean
  /** Human-readable reason when the active viewer series fails the mask series filter. */
  seriesFilterMismatch: string | null
}

export function MaskModulePanel({
  schema,
  moduleId,
  seriesId,
  masks,
  activeMaskInstanceId,
  onSelectMaskInstance,
  onActivateSingleLabel,
  onCreateMaskInstance,
  onDeleteMaskInstance,
  drawingDisabled,
  onDrawingDisabled,
  brushSize,
  onBrushSize,
  filledPen,
  onFilledPen,
  eraseMode,
  onEraseMode,
  samMode,
  onSamMode,
  samAvailable,
  samPending,
  samScribbleMode,
  onSamScribbleMode,
  samConfidence,
  onSamConfidence,
  maskOpacityById,
  onMaskOpacityChange,
  onMaskOpacityCommit,
  onMaskVisibilityToggle,
  isEditable,
  seriesFilterMismatch
}: MaskModulePanelProps) {
  const [instancesCollapsed, setInstancesCollapsed] = useState(false)
  const [expandedLabelGroups, toggleLabelGroupExpanded] = useToggleSet<string>()
  const [expandedInstanceGroups, toggleInstanceGroupExpanded] = useToggleSet<string>()

  const groups = useMemo(() => normalizeMaskGroups(schema), [schema])
  const allLabels = useMemo(() => flattenMaskLabels(schema), [schema])
  const isGrouped = groups.length > 1 || (groups.length === 1 && groups[0]!.name !== "")

  const moduleMasks = useMemo(
    () =>
      masks
        .filter((mask) => mask.projectModuleId === moduleId && mask.seriesId === seriesId)
        .sort((a, b) => {
          const aValue = a.labelValue ?? Number.MAX_SAFE_INTEGER
          const bValue = b.labelValue ?? Number.MAX_SAFE_INTEGER
          if (aValue !== bValue) return aValue - bValue
          if (a.instanceOrdinal !== b.instanceOrdinal) {
            return a.instanceOrdinal - b.instanceOrdinal
          }
          return a.id.localeCompare(b.id)
        }),
    [masks, moduleId, seriesId]
  )

  const masksByLabel = useMemo(() => {
    const grouped = new Map<string, MaskAnnotationState[]>()
    for (const mask of moduleMasks) {
      const key = mask.labelName
      if (!key) continue
      const existing = grouped.get(key)
      if (existing) {
        existing.push(mask)
      } else {
        grouped.set(key, [mask])
      }
    }
    return grouped
  }, [moduleMasks])

  const labelDefByKey = useMemo(() => {
    const map = new Map<string, MaskLabel>()
    for (const lbl of allLabels) {
      map.set(lbl.key, lbl)
    }
    return map
  }, [allLabels])

  // Map label key -> group key for instance grouping
  const labelToGroupKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const group of groups) {
      for (const lbl of group.labels) {
        map.set(lbl.key, group.key)
      }
    }
    return map
  }, [groups])

  type InstanceGroupBucket = { group: MaskGroup; masks: MaskAnnotationState[] }

  const instanceGroups = useMemo((): InstanceGroupBucket[] => {
    if (!isGrouped) {
      // Single default group — flat list
      return moduleMasks.length > 0 ? [{ group: groups[0]!, masks: moduleMasks }] : []
    }
    const buckets = new Map<string, MaskAnnotationState[]>()
    for (const group of groups) buckets.set(group.key, [])
    for (const mask of moduleMasks) {
      const gk = mask.labelName ? labelToGroupKey.get(mask.labelName) : undefined
      if (gk) buckets.get(gk)?.push(mask)
    }
    const result: InstanceGroupBucket[] = []
    for (const group of groups) {
      const gMasks = buckets.get(group.key)!
      if (gMasks.length > 0) result.push({ group, masks: gMasks })
    }
    return result
  }, [groups, isGrouped, labelToGroupKey, moduleMasks])

  const DEFAULT_OPACITY_PERCENT = 70

  function getOpacityPercent(maskId: string): number {
    return maskOpacityById[maskId] ?? DEFAULT_OPACITY_PERCENT
  }

  // Derive the active tool from the boolean flags
  const activeTool: MaskTool | null = drawingDisabled
    ? null
    : samMode
      ? "sam_box"
      : samScribbleMode
        ? "sam_scribble"
        : eraseMode
          ? "erase"
          : activeMaskInstanceId
            ? "paint"
            : null

  function handleToolChange(tool: MaskTool | null) {
    if (!tool || tool === activeTool) {
      // Toggle off: deactivate all drawing tools
      onEraseMode(false)
      onSamMode(false)
      onSamScribbleMode(false)
      onDrawingDisabled(true)
      return
    }
    // Activating a tool — ensure drawing is enabled
    onDrawingDisabled(false)
    switch (tool) {
      case "paint":
        onEraseMode(false)
        onSamMode(false)
        onSamScribbleMode(false)
        break
      case "erase":
        onEraseMode(true)
        onSamMode(false)
        onSamScribbleMode(false)
        break
      case "sam_box":
        onSamMode(true)
        onSamScribbleMode(false)
        onEraseMode(false)
        break
      case "sam_scribble":
        onSamScribbleMode(true)
        onSamMode(false)
        onEraseMode(false)
        break
    }
  }

  const toolItems = samAvailable
    ? MASK_TOOL_ITEMS
    : MASK_TOOL_ITEMS.filter((t) => t.value === "paint" || t.value === "erase")

  // Context-sensitive help text
  const helpText = (() => {
    if (activeTool === "sam_box") return "Click or drag a box to segment with SAM"
    if (activeTool === "sam_scribble") return "Scribble on the target area for SAM segmentation"
    if (activeTool === "erase") return "Erase mode removes voxels from the active mask"
    if (!activeMaskInstanceId) return "Select or create an instance to enable drawing"
    if (drawingDisabled) return "Drawing tools paused — click a tool to resume"
    return "Paint mode enabled for selected mask instance"
  })()

  function renderLabelRow(lbl: MaskLabel) {
    const labelMasks = masksByLabel.get(lbl.key) ?? []
    const isSingle = lbl.instance_policy.mode === "single"
    const singleMask = isSingle ? labelMasks[0] : null
    const isLabelActive = isSingle
      ? singleMask?.id === activeMaskInstanceId
      : labelMasks.some((mask) => mask.id === activeMaskInstanceId)

    return (
      <div key={lbl.key} className="space-y-1">
        <div className="flex items-center gap-2">
          {isSingle ? (
            <button
              disabled={!isEditable}
              onClick={() => {
                onDrawingDisabled(false)
                onActivateSingleLabel(lbl.key)
              }}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md border text-left transition-colors ${
                isLabelActive ? "border-primary/60 bg-primary/10" : "border-border bg-surface-raised hover:bg-accent/40"
              }`}
            >
              <span
                className="size-3 rounded-sm shrink-0 border border-white/20"
                style={{ backgroundColor: lbl.color }}
              />
              <span className="text-[11px] font-medium truncate flex-1">{lbl.label}</span>
            </button>
          ) : (
            <div
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md border transition-colors ${
                isLabelActive ? "border-primary/60 bg-primary/10" : "border-border bg-surface-raised"
              }`}
            >
              <span
                className="size-3 rounded-sm shrink-0 border border-white/20"
                style={{ backgroundColor: lbl.color }}
              />
              <span className="text-[11px] font-medium truncate flex-1">{lbl.label}</span>
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 ml-auto text-[10px] shrink-0"
                disabled={!isEditable}
                onClick={() => {
                  onDrawingDisabled(false)
                  onCreateMaskInstance(lbl.key)
                }}
              >
                + New
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto p-3 space-y-4">
      {/* Series filter mismatch warning */}
      {seriesFilterMismatch && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 flex items-start gap-2 text-[11px] text-amber-200">
          <Info className="size-3.5 shrink-0 mt-0.5 text-amber-400" />
          <div>
            <span className="font-medium">Series not eligible for this mask module.</span>
            <br />
            <span className="text-[10px] text-amber-300/80">{seriesFilterMismatch}</span>
          </div>
        </div>
      )}

      {/* Tool selector */}
      <div className="space-y-2">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Tool</div>
        <TooltipProvider>
          <ToggleGroup variant="outline" size="sm" className="w-full">
            {toolItems.map(({ value, icon: Icon, tip }) => (
              <Tooltip key={value}>
                <TooltipTrigger render={<span className="flex-1" />}>
                  <ToggleGroupItem
                    value={value}
                    pressed={activeTool === value}
                    onPressedChange={() => handleToolChange(activeTool === value ? null : value)}
                    disabled={
                      !isEditable || (!activeMaskInstanceId && (value === "sam_box" || value === "sam_scribble"))
                    }
                    className={`h-7 w-full p-0 ${
                      activeTool === value
                        ? "border-primary/70 bg-primary/20 text-primary aria-pressed:bg-primary/20"
                        : ""
                    }`}
                  >
                    <Icon className="size-3.5" />
                    {samPending &&
                    ((value === "sam_box" && samMode) || (value === "sam_scribble" && samScribbleMode)) ? (
                      <span className="ml-1 text-[10px]">...</span>
                    ) : null}
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent side="bottom">{tip}</TooltipContent>
              </Tooltip>
            ))}
          </ToggleGroup>
        </TooltipProvider>
      </div>

      {/* Tool-specific settings */}
      {(activeTool === "paint" || activeTool === "erase") && (
        <div className="space-y-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-2">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>Brush size</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                aria-label="Brush size in voxels"
                min={1}
                max={48}
                step={1}
                disabled={!isEditable}
                value={brushSize[0] ?? 2}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(48, Math.round(Number(e.target.value) || 1)))
                  onBrushSize([v])
                }}
                className="w-12 h-5 text-[10px] font-mono tabular-nums text-right bg-transparent border border-border rounded px-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <span className="font-mono tabular-nums">px</span>
            </div>
          </div>
          <Slider
            value={brushSize}
            min={1}
            max={48}
            step={1}
            disabled={!isEditable}
            onValueChange={(value) => onBrushSize(Array.isArray(value) ? [...value] : [value])}
          />
          {activeTool === "paint" && (
            <div className="pt-1.5 border-t border-border/50 mt-1.5">
              <Label
                htmlFor={`filled-pen-${moduleId}`}
                className="cursor-pointer text-[10px] flex items-center gap-1.5"
              >
                <Checkbox
                  id={`filled-pen-${moduleId}`}
                  checked={filledPen}
                  onCheckedChange={(checked) => onFilledPen(Boolean(checked))}
                  disabled={!isEditable}
                />
                Filled brush
              </Label>
            </div>
          )}
        </div>
      )}

      {(activeTool === "sam_box" || activeTool === "sam_scribble") && (
        <div className="space-y-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-2">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>SAM Confidence</span>
            <span className="font-mono tabular-nums">{Math.round(samConfidence * 100)}%</span>
          </div>
          <Slider
            value={[samConfidence * 100]}
            onValueChange={(value) => {
              const vals = Array.isArray(value) ? value : [value]
              onSamConfidence((vals[0] ?? 50) / 100)
            }}
            max={95}
            min={5}
            step={5}
            disabled={!isEditable}
          />
          <div className="flex justify-between text-[9px] text-muted-foreground">
            <span>More sensitive</span>
            <span>More precise</span>
          </div>
        </div>
      )}

      {/* Labels */}
      <div className="space-y-2">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Labels</div>
        {isGrouped ? (
          groups.map((group) => {
            const isGroupCollapsed = !expandedLabelGroups.has(group.key)
            return (
              <div key={group.key} className="space-y-1">
                <div className="flex items-center gap-1">
                  <button
                    className="size-5 flex items-center justify-center rounded hover:bg-accent/40 transition-colors"
                    title={isGroupCollapsed ? `Expand ${group.name}` : `Collapse ${group.name}`}
                    onClick={() => toggleLabelGroupExpanded(group.key)}
                  >
                    {isGroupCollapsed ? (
                      <ChevronRight className="size-3 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="size-3 text-muted-foreground" />
                    )}
                  </button>
                  <span className="text-[10px] font-semibold truncate">{group.name}</span>
                </div>
                {!isGroupCollapsed && (
                  <div className="pl-1.5 space-y-1">{group.labels.map((lbl) => renderLabelRow(lbl))}</div>
                )}
              </div>
            )
          })
        ) : (
          <div className="space-y-1">{allLabels.map((lbl) => renderLabelRow(lbl))}</div>
        )}
        <p className="text-[10px] text-muted-foreground">{helpText}</p>
      </div>

      {/* Mask Instances */}
      <div className="space-y-2">
        <CollapsibleSectionHeader
          collapsed={instancesCollapsed}
          onToggle={() => setInstancesCollapsed((c) => !c)}
          label="Instances"
          count={moduleMasks.length}
        />
        {!instancesCollapsed &&
          (moduleMasks.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-2.5 py-2 text-[10px] text-muted-foreground">
              No mask instances yet
            </div>
          ) : isGrouped ? (
            <div className="space-y-2">
              {instanceGroups.map((bucket) => (
                <div key={bucket.group.key} className="space-y-1">
                  <CollapsibleSectionHeader
                    collapsed={!expandedInstanceGroups.has(bucket.group.key)}
                    onToggle={() => toggleInstanceGroupExpanded(bucket.group.key)}
                    label={bucket.group.name || "Instances"}
                    labelClassName="text-[10px] font-semibold truncate"
                    count={bucket.masks.length}
                  />
                  {expandedInstanceGroups.has(bucket.group.key) && (
                    <div className="pl-1.5 space-y-2">
                      {bucket.masks.map((mask) => (
                        <MaskInstanceCard
                          key={mask.id}
                          mask={mask}
                          labelDef={labelDefByKey.get(mask.labelName ?? "") ?? null}
                          isActive={mask.id === activeMaskInstanceId}
                          opacityPercent={getOpacityPercent(mask.id)}
                          isEditable={isEditable}
                          onSelect={() => {
                            const nextId = mask.id === activeMaskInstanceId ? null : mask.id
                            if (nextId) onDrawingDisabled(false)
                            onSelectMaskInstance(nextId)
                          }}
                          onDelete={onDeleteMaskInstance}
                          onVisibilityToggle={() => onMaskVisibilityToggle(mask.id)}
                          onOpacityChange={(pct) => onMaskOpacityChange(mask.id, pct)}
                          onOpacityCommit={(pct) => onMaskOpacityCommit(mask.id, pct)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {allLabels.map((lbl) => {
                const labelMasks = masksByLabel.get(lbl.key) ?? []
                if (labelMasks.length === 0) return null
                return labelMasks.map((mask) => (
                  <MaskInstanceCard
                    key={mask.id}
                    mask={mask}
                    labelDef={labelDefByKey.get(mask.labelName ?? "") ?? null}
                    isActive={mask.id === activeMaskInstanceId}
                    opacityPercent={getOpacityPercent(mask.id)}
                    isEditable={isEditable}
                    onSelect={() => {
                      const nextId = mask.id === activeMaskInstanceId ? null : mask.id
                      if (nextId) onDrawingDisabled(false)
                      onSelectMaskInstance(nextId)
                    }}
                    onDelete={onDeleteMaskInstance}
                    onVisibilityToggle={() => onMaskVisibilityToggle(mask.id)}
                    onOpacityChange={(pct) => onMaskOpacityChange(mask.id, pct)}
                    onOpacityCommit={(pct) => onMaskOpacityCommit(mask.id, pct)}
                  />
                ))
              })}
            </div>
          ))}
      </div>
    </div>
  )
}
